<template>
  <div class="editor-area flex-1 bg-base-200 relative overflow-hidden h-full">
    <div ref="stageContainer" class="w-full h-full pixelated"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import Konva from "konva";
import { CANVAS_CONFIG, EditorMode } from "./config";
import type {
  GridInfo,
  Point,
  TouchInfo,
  Frame,
  MultiTouchState,
} from "./types";
import { useViewport } from "./composables/useViewport";

interface Props {
  currentFrame: Frame | undefined;
  mode: EditorMode;
  color: string;
  fill?: string;
}

interface Emits {
  (e: "history-add", imageData: ImageData): void;
  (e: "frame-modified"): void;
  (e: "frame-updated", imageData: ImageData): void;
  (e: "undo"): void;
  (e: "redo"): void;
  (e: "mode-change", mode: EditorMode): void;
  (e: "color-picked", color: string, fill?: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

type MyLayer = { gridInfo: GridInfo } & Konva.Layer;

const stageContainer = ref<HTMLDivElement>();
let stage: Konva.Stage | null = null;
let layer: MyLayer | null = null;
let multiTouchDetectionTimer: ReturnType<typeof setTimeout> | null = null;

// 将这些状态变量提升到模块级别，以便在watch中访问
let isDrawing = false;
let lastPointerPosition: Point | null = null;
let touchInfo: TouchInfo = { initialDistance: 0, initialScale: 1 };

// 多点触控状态管理
let multiTouchState: MultiTouchState = {
  isDetecting: false,
  firstTouchTime: 0,
  touchStartCount: 0,
  hasMultipleFingers: false,
  pendingDrawingOperations: [],
};
let zoomRafId: number | null = null;

const viewport = useViewport();

// 多点触控状态重置函数
const resetMultiTouchState = () => {
  multiTouchState.isDetecting = false;
  multiTouchState.firstTouchTime = 0;
  multiTouchState.touchStartCount = 0;
  multiTouchState.hasMultipleFingers = false;
  multiTouchState.pendingDrawingOperations = [];
  if (multiTouchDetectionTimer) {
    clearTimeout(multiTouchDetectionTimer);
    multiTouchDetectionTimer = null;
  }
};

onMounted(async () => {
  await nextTick();
  initializeKonva();

  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  // 清理多点触控定时器
  if (multiTouchDetectionTimer) {
    clearTimeout(multiTouchDetectionTimer);
  }
  // 清理缩放动画帧
  if (zoomRafId) {
    cancelAnimationFrame(zoomRafId);
  }
  stage?.destroy();
});

const initializeKonva = () => {
  if (!stageContainer.value) return;

  const container = stageContainer.value;
  const containerRect = container.getBoundingClientRect();

  stage = new Konva.Stage({
    container: stageContainer.value,
    width: containerRect.width,
    height: containerRect.height,
  });

  // 设置canvas像素化渲染
  const canvas = stage.toCanvas();
  canvas.style.imageRendering = "pixelated";

  layer = new Konva.Layer() as MyLayer;
  stage.add(layer);

  // 保存初始视图状态
  viewport.saveInitialState({
    x: stage.x(),
    y: stage.y(),
    scale: stage.scaleX(),
  });

  loadCurrentFrame();
  setupEventListeners();
};

const loadCurrentFrame = async () => {
  if (!layer || !stage || !props.currentFrame) return;

  // 清除层
  layer.destroyChildren();

  // 创建像素网格
  createPixelGrid();

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    // 将图片绘制到隐藏的canvas上以获取像素数据
    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_CONFIG.SPRITE_WIDTH;
    canvas.height = CANVAS_CONFIG.SPRITE_HEIGHT;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(
      img,
      0,
      0,
      CANVAS_CONFIG.SPRITE_WIDTH,
      CANVAS_CONFIG.SPRITE_HEIGHT
    );

    // 获取像素数据并创建像素方块
    const imageData = ctx.getImageData(
      0,
      0,
      CANVAS_CONFIG.SPRITE_WIDTH,
      CANVAS_CONFIG.SPRITE_HEIGHT
    );
    createPixelBlocks(imageData);

    // 如果没有历史记录，保存初始状态（不更新frame URL）
    if (props.currentFrame && props.currentFrame.history.length === 0) {
      // 使用setTimeout确保像素块创建完成后再保存
      setTimeout(() => {
        saveToHistory(true); // skipFrameUpdate = true
      }, 0);
    }
  };
  img.src = props.currentFrame.url;
};

const createPixelGrid = () => {
  if (!layer || !stage) return;

  const centerX = stage.width() / 2;
  const centerY = stage.height() / 2;
  const pixelSize = Math.min(stage.width() / 60, stage.height() / 110); // 留出边距
  const gridWidth = CANVAS_CONFIG.SPRITE_WIDTH * pixelSize;
  const gridHeight = CANVAS_CONFIG.SPRITE_HEIGHT * pixelSize;
  const startX = centerX - gridWidth / 2;
  const startY = centerY - gridHeight / 2;

  // 创建透明背景棋盘格
  const checkerSize = pixelSize * CANVAS_CONFIG.CHECKER_SIZE_MULTIPLIER;
  for (let y = 0; y < Math.ceil(gridHeight / checkerSize); y++) {
    for (let x = 0; x < Math.ceil(gridWidth / checkerSize); x++) {
      if ((x + y) % 2 === 1) {
        const checker = new Konva.Rect({
          x: startX + x * checkerSize,
          y: startY + y * checkerSize,
          width: Math.min(checkerSize, gridWidth - x * checkerSize),
          height: Math.min(checkerSize, gridHeight - y * checkerSize),
          fill: "hsl(var(--bc) / 0.05)",
        });
        layer.add(checker);
      }
    }
  }

  // 创建主背景
  const background = new Konva.Rect({
    x: startX,
    y: startY,
    width: gridWidth,
    height: gridHeight,
    fill: "transparent",
    stroke: "hsl(var(--bc) / 0.2)",
    strokeWidth: 2,
  });
  layer.add(background);

  // 创建网格线
  for (let i = 0; i <= CANVAS_CONFIG.SPRITE_WIDTH; i++) {
    const line = new Konva.Line({
      points: [
        startX + i * pixelSize,
        startY,
        startX + i * pixelSize,
        startY + gridHeight,
      ],
      stroke: "hsl(var(--bc) / 0.1)",
      strokeWidth: 0.5,
    });
    layer.add(line);
  }

  for (let i = 0; i <= CANVAS_CONFIG.SPRITE_HEIGHT; i++) {
    const line = new Konva.Line({
      points: [
        startX,
        startY + i * pixelSize,
        startX + gridWidth,
        startY + i * pixelSize,
      ],
      stroke: "hsl(var(--bc) / 0.1)",
      strokeWidth: 0.5,
    });
    layer.add(line);
  }

  // 保存网格信息供绘制使用
  const gridInfo: GridInfo = {
    startX,
    startY,
    pixelSize,
    gridWidth,
    gridHeight,
  };
  layer.gridInfo = gridInfo;
};

const createPixelBlocks = (imageData: ImageData) => {
  if (!layer || !stage) return;

  const gridInfo = layer.gridInfo as GridInfo;
  if (!gridInfo) return;

  const { startX, startY, pixelSize } = gridInfo;
  const data = imageData.data;

  for (let y = 0; y < CANVAS_CONFIG.SPRITE_HEIGHT; y++) {
    for (let x = 0; x < CANVAS_CONFIG.SPRITE_WIDTH; x++) {
      const index = (y * CANVAS_CONFIG.SPRITE_WIDTH + x) * 4;
      const r = data[index];
      const g = data[index + 1];
      const b = data[index + 2];
      const a = data[index + 3];

      // 只绘制非透明像素
      if (a > 0) {
        const pixel = new Konva.Rect({
          x: startX + x * pixelSize,
          y: startY + y * pixelSize,
          width: pixelSize,
          height: pixelSize,
          fill: `rgba(${r}, ${g}, ${b}, ${a / 255})`,
        });
        pixel.setAttrs({ pixelX: x, pixelY: y }); // 保存像素坐标
        layer.add(pixel);
      }
    }
  }

  layer.batchDraw();
};

const setupEventListeners = () => {
  if (!stage) return;

  // 多点触控检测函数
  const startMultiTouchDetection = () => {
    multiTouchState.isDetecting = true;
    multiTouchState.firstTouchTime = Date.now();
    multiTouchState.touchStartCount = 1;
    multiTouchState.hasMultipleFingers = false;
    multiTouchState.pendingDrawingOperations = [];

    // 清除之前的定时器
    if (multiTouchDetectionTimer) {
      clearTimeout(multiTouchDetectionTimer);
    }

    // 设置50ms检测超时
    multiTouchDetectionTimer = setTimeout(() => {
      // 检测结束，如果没有多点触控，执行待处理的绘画操作
      if (
        !multiTouchState.hasMultipleFingers &&
        multiTouchState.pendingDrawingOperations.length > 0
      ) {
        multiTouchState.pendingDrawingOperations.forEach((op) => {
          drawPixel(op.x, op.y);
        });
      }
      resetMultiTouchState();
    }, CANVAS_CONFIG.MULTI_TOUCH_DETECTION_TIMEOUT);
  };

  const handleAdditionalTouch = (evt: TouchEvent) => {
    if (multiTouchState.isDetecting) {
      multiTouchState.touchStartCount++;
      if (multiTouchState.touchStartCount >= 2) {
        multiTouchState.hasMultipleFingers = true;

        // 立即停止绘画状态
        isDrawing = false;
        lastPointerPosition = null;

        // 清除待处理的绘画操作，防止意外绘制
        multiTouchState.pendingDrawingOperations = [];

        // 如果有双指触摸，立即初始化缩放参数以便无缝切换
        if (evt.touches && evt.touches.length >= 2) {
          const touch1 = evt.touches[0];
          const touch2 = evt.touches[1];
          const currentDistance = Math.sqrt(
            Math.pow(touch2.clientX - touch1.clientX, 2) +
              Math.pow(touch2.clientY - touch1.clientY, 2)
          );

          // 只有距离合理时才初始化
          if (currentDistance > 50 && currentDistance < 500) {
            touchInfo.initialDistance = currentDistance;
            touchInfo.initialScale = stage!.scaleX();
          }
        }

        // 清除检测定时器
        if (multiTouchDetectionTimer) {
          clearTimeout(multiTouchDetectionTimer);
          multiTouchDetectionTimer = null;
        }

        // 自动切换到缩放模式
        emit("mode-change", EditorMode.ZOOM);
      }
    }
  };

  // 编辑模式事件
  stage.on("mousedown touchstart", (e) => {
    if (props.mode === EditorMode.ZOOM) {
      // 缩放模式 - 记录初始状态
      const evt = e.evt as TouchEvent | MouseEvent;
      if (
        evt instanceof TouchEvent &&
        evt.touches &&
        evt.touches.length === 2
      ) {
        // 双指缩放 - 防止模式切换后立即触发异常缩放
        e.evt.preventDefault();
        const touch1 = evt.touches[0];
        const touch2 = evt.touches[1];
        const currentDistance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2)
        );

        // 总是更新touchInfo以确保缩放的连贯性
        if (currentDistance > 50 && currentDistance < 500) {
          touchInfo.initialDistance = currentDistance;
          touchInfo.initialScale = stage!.scaleX();
        }
      } else if (evt instanceof TouchEvent && evt.touches.length === 1) {
        // 单指拖动开始
        isDrawing = true;
        const pos = stage!.getPointerPosition()!;
        lastPointerPosition = pos;
      } else if (evt instanceof MouseEvent) {
        // 鼠标拖动开始
        isDrawing = true;
        const pos = stage!.getPointerPosition()!;
        lastPointerPosition = pos;
      }
      return;
    } else if (props.mode === EditorMode.EYEDROPPER) {
      const pos = stage!.getPointerPosition()!;
      pickColor(pos.x, pos.y);
      return;
    }

    const evt = e.evt as TouchEvent | MouseEvent;
    // 处理触摸事件的多点检测
    if (evt instanceof TouchEvent) {
      // 如果已经检测到多点触控，不执行任何绘画操作
      if (multiTouchState.hasMultipleFingers) {
        return;
      }

      if (!multiTouchState.isDetecting) {
        // 第一次触摸，开始检测
        startMultiTouchDetection();
      } else {
        // 额外的触摸，处理多点触控
        handleAdditionalTouch(evt);
        return; // 如果是多点触控，直接返回不执行绘画
      }

      // 如果正在多点检测期间，暂存绘画操作而不是立即执行
      if (multiTouchState.isDetecting && !multiTouchState.hasMultipleFingers) {
        const pos = stage!.getPointerPosition()!;

        // 其他工具：暂存绘画操作
        multiTouchState.pendingDrawingOperations.push({
          x: pos.x,
          y: pos.y,
          timestamp: Date.now(),
        });
        isDrawing = true;
        lastPointerPosition = pos;
        return; // 暂存操作，不立即绘画
      }
    }

    // 鼠标事件或已确认为单点触摸，正常处理
    if (!multiTouchState.hasMultipleFingers) {
      const pos = stage!.getPointerPosition()!;

      // 其他工具：正常绘制
      isDrawing = true;
      lastPointerPosition = pos;
      drawPixel(pos.x, pos.y);
    }
  });

  stage.on("mousemove touchmove", (e) => {
    if (props.mode === EditorMode.PAINT || props.mode === EditorMode.ERASER) {
      if (!isDrawing) return;

      // 如果在多点检测期间或已检测到多点触控，不执行绘画
      if (multiTouchState.isDetecting || multiTouchState.hasMultipleFingers) {
        return;
      }

      const pos = stage!.getPointerPosition()!;
      drawPixel(pos.x, pos.y);
      lastPointerPosition = pos;
    } else if (props.mode === EditorMode.ZOOM) {
      const evt = e.evt as TouchEvent | MouseEvent;

      if (evt instanceof TouchEvent && evt.touches.length === 2) {
        // 双指缩放
        e.evt.preventDefault();

        // 确保touchInfo已正确初始化
        if (!touchInfo.initialDistance || touchInfo.initialDistance === 0) {
          return;
        }

        const touch1 = evt.touches[0];
        const touch2 = evt.touches[1];
        const currentDistance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2)
        );

        // 防止异常的距离值导致缩放跳跃
        if (currentDistance < 10 || currentDistance > 1000) {
          return;
        }

        const newScale =
          (currentDistance / touchInfo.initialDistance) *
          touchInfo.initialScale;
        const clampedScale = Math.max(
          CANVAS_CONFIG.MIN_SCALE,
          Math.min(CANVAS_CONFIG.MAX_SCALE, newScale)
        );

        // 检查缩放变化是否足够大，避免微小变化导致频繁重绘
        const currentScale = stage!.scaleX();
        const scaleChange = Math.abs(clampedScale - currentScale);
        if (scaleChange < 0.01) {
          return; // 变化太小，跳过此次更新
        }

        // 计算双指中心点（相对于stage容器）
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        const containerRect = stage!.container().getBoundingClientRect();
        const stageCenter = {
          x: centerX - containerRect.left,
          y: centerY - containerRect.top,
        };

        // 应用缩放
        stage!.scale({ x: clampedScale, y: clampedScale });

        // 计算位置调整以保持中心点固定
        const newPos = viewport.calculateZoomPosition(
          stageCenter,
          currentScale,
          clampedScale,
          stage!
        );

        // 简化边界限制 - 只在必要时应用
        stage!.position(newPos);

        // 使用防抖的requestAnimationFrame来优化重绘性能
        if (zoomRafId) {
          cancelAnimationFrame(zoomRafId);
        }
        zoomRafId = requestAnimationFrame(() => {
          stage!.batchDraw();
          zoomRafId = null;
        });
      } else if (
        evt instanceof MouseEvent ||
        (evt.touches && evt.touches.length === 1)
      ) {
        // 单指/鼠标拖动
        if (isDrawing && lastPointerPosition) {
          const pos = stage!.getPointerPosition()!;
          const dx = pos.x - lastPointerPosition.x;
          const dy = pos.y - lastPointerPosition.y;

          // 计算新位置并应用边界限制
          const newX = stage!.x() + dx;
          const newY = stage!.y() + dy;
          const gridInfo = layer!.gridInfo;

          if (gridInfo) {
            const constrainedPos = viewport.constrainViewport(
              newX,
              newY,
              stage!.scaleX(),
              stage!.width(),
              stage!.height(),
              gridInfo
            );

            stage!.x(constrainedPos.x);
            stage!.y(constrainedPos.y);
            stage!.batchDraw();
          }

          // 更新最后位置
          lastPointerPosition = pos;
        }
      }
    }
  });

  stage.on("mouseup touchend", (e) => {
    const evt = e.evt as TouchEvent | MouseEvent;

    if (
      (props.mode === EditorMode.PAINT || props.mode === EditorMode.ERASER) &&
      isDrawing
    ) {
      // 检查是否是触摸事件且还有其他活跃的触摸点
      if (evt instanceof TouchEvent && evt.touches && evt.touches.length > 0) {
        // 还有其他触摸点，不重置状态
        return;
      }

      isDrawing = false;
      lastPointerPosition = null;

      // 如果不在多点检测期间，保存历史记录
      if (!multiTouchState.isDetecting && !multiTouchState.hasMultipleFingers) {
        saveToHistory();
        emit("frame-modified");
      }
    } else if (props.mode === EditorMode.ZOOM) {
      // 检查是否是触摸事件且还有其他活跃的触摸点
      if (evt instanceof TouchEvent && evt.touches && evt.touches.length > 0) {
        return;
      }

      isDrawing = false;
      lastPointerPosition = null;
    }

    // 如果是触摸事件且没有剩余触摸点，重置多点触控状态
    if (
      evt instanceof TouchEvent &&
      (!evt.touches || evt.touches.length === 0)
    ) {
      resetMultiTouchState();
    }
  });

  // 鼠标滚轮缩放（桌面端）
  stage.on("wheel", (e) => {
    if (props.mode !== EditorMode.ZOOM) return;

    e.evt.preventDefault();
    const stage_ = stage!;
    const oldScale = stage_.scaleX();
    const pointer = stage_.getPointerPosition()!;

    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = Math.max(
      CANVAS_CONFIG.MIN_SCALE,
      Math.min(
        CANVAS_CONFIG.MAX_SCALE,
        oldScale * Math.pow(CANVAS_CONFIG.SCALE_FACTOR, direction)
      )
    );

    stage_.scale({ x: newScale, y: newScale });

    const newPos = viewport.calculateZoomPosition(
      pointer,
      oldScale,
      newScale,
      stage_
    );

    // 简化边界限制，直接应用位置
    stage_.position(newPos);

    // 使用防抖的requestAnimationFrame优化重绘
    if (zoomRafId) {
      cancelAnimationFrame(zoomRafId);
    }
    zoomRafId = requestAnimationFrame(() => {
      stage_.batchDraw();
      zoomRafId = null;
    });
  });
};

const pickColor = (x: number, y: number) => {
  if (!layer || !stage) return;

  const gridInfo = layer.gridInfo as GridInfo;
  if (!gridInfo) return;

  const { startX, startY, pixelSize, gridWidth, gridHeight } = gridInfo;

  // 将鼠标坐标转换为图层坐标（考虑舞台的缩放和偏移）
  const transform = stage.getAbsoluteTransform().copy();
  transform.invert();
  const pos = transform.point({ x, y });

  // 检查是否在网格范围内
  if (
    pos.x < startX ||
    pos.x > startX + gridWidth ||
    pos.y < startY ||
    pos.y > startY + gridHeight
  ) {
    return;
  }

  // 计算像素坐标
  const pixelX = Math.floor((pos.x - startX) / pixelSize);
  const pixelY = Math.floor((pos.y - startY) / pixelSize);

  // 确保在有效范围内
  if (
    pixelX < 0 ||
    pixelX >= CANVAS_CONFIG.SPRITE_WIDTH ||
    pixelY < 0 ||
    pixelY >= CANVAS_CONFIG.SPRITE_HEIGHT
  ) {
    return;
  }

  // 查找该位置的像素块
  const existingPixel = layer.children.find(
    (child) => child.attrs.pixelX === pixelX && child.attrs.pixelY === pixelY
  );

  if (existingPixel) {
    const fill = (existingPixel as Konva.Rect).fill();
    if (fill && fill !== "transparent") {
      // 转换颜色格式为十六进制
      const color = convertToHexColor(fill as string);
      if (color) {
        emit("color-picked", color, fill as string);
      }
    }
  }
};

const convertToHexColor = (color: string): string | null => {
  console.log("color", color);
  if (color.startsWith("#")) {
    return color;
  }

  if (color.startsWith("rgba")) {
    // 解析 rgba(r, g, b, a) 格式
    const match = color.match(
      /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/
    );
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      return `#${r.toString(16).padStart(2, "0")}${g
        .toString(16)
        .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    }
  }

  if (color.startsWith("rgb")) {
    // 解析 rgb(r, g, b) 格式
    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      return `#${r.toString(16).padStart(2, "0")}${g
        .toString(16)
        .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    }
  }

  return null;
};

const drawPixel = (x: number, y: number) => {
  if (!layer || !stage) return;

  const gridInfo = layer.gridInfo as GridInfo;
  if (!gridInfo) return;

  const { startX, startY, pixelSize, gridWidth, gridHeight } = gridInfo;

  // 将鼠标坐标转换为图层坐标（考虑舞台的缩放和偏移）
  const transform = stage.getAbsoluteTransform().copy();
  transform.invert();
  const pos = transform.point({ x, y });

  // 检查是否在网格范围内
  if (
    pos.x < startX ||
    pos.x > startX + gridWidth ||
    pos.y < startY ||
    pos.y > startY + gridHeight
  ) {
    return;
  }

  // 计算像素坐标
  const pixelX = Math.floor((pos.x - startX) / pixelSize);
  const pixelY = Math.floor((pos.y - startY) / pixelSize);

  // 确保在有效范围内
  if (
    pixelX < 0 ||
    pixelX >= CANVAS_CONFIG.SPRITE_WIDTH ||
    pixelY < 0 ||
    pixelY >= CANVAS_CONFIG.SPRITE_HEIGHT
  ) {
    return;
  }

  const actualX = startX + pixelX * pixelSize;
  const actualY = startY + pixelY * pixelSize;

  if (props.mode === EditorMode.ERASER) {
    // 擦除：查找并删除该位置的像素
    const existingPixel = layer.children.find(
      (child) => child.attrs.pixelX === pixelX && child.attrs.pixelY === pixelY
    );
    if (existingPixel) {
      existingPixel.destroy();
    }
  } else {
    // 绘制：先删除现有像素，再添加新像素
    const existingPixel = layer.children.find(
      (child) => child.attrs.pixelX === pixelX && child.attrs.pixelY === pixelY
    );
    if (existingPixel) {
      existingPixel.destroy();
    }

    const pixel = new Konva.Rect({
      x: actualX,
      y: actualY,
      width: pixelSize,
      height: pixelSize,
      fill: props.fill || props.color,
    });
    pixel.setAttrs({ pixelX, pixelY });
    layer.add(pixel);
  }

  layer.batchDraw();
};

const saveToHistory = (skipFrameUpdate = false) => {
  if (!stage || !layer) return;

  // 获取像素数据并转换为sprite尺寸的数据
  const spriteImageData = getCurrentSpriteImageData();
  if (spriteImageData) {
    emit("history-add", spriteImageData);
    if (!skipFrameUpdate) {
      emit("frame-updated", spriteImageData);
    }
  }
};

const getCurrentSpriteImageData = (): ImageData | null => {
  if (!layer) return null;

  const gridInfo = layer.gridInfo as GridInfo;
  if (!gridInfo) return null;

  // 创建sprite尺寸的canvas
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_CONFIG.SPRITE_WIDTH;
  canvas.height = CANVAS_CONFIG.SPRITE_HEIGHT;
  const ctx = canvas.getContext("2d")!;

  // 遍历所有像素块并绘制到canvas上
  layer.children.forEach((child) => {
    if (child.attrs.pixelX !== undefined && child.attrs.pixelY !== undefined) {
      const pixelX = child.attrs.pixelX;
      const pixelY = child.attrs.pixelY;
      const fill = (child as Konva.Rect).fill();

      if (fill && fill !== "transparent") {
        ctx.fillStyle = fill;
        ctx.fillRect(pixelX, pixelY, 1, 1);
      }
    }
  });

  return ctx.getImageData(
    0,
    0,
    CANVAS_CONFIG.SPRITE_WIDTH,
    CANVAS_CONFIG.SPRITE_HEIGHT
  );
};

const restoreFromHistory = (imageData: ImageData) => {
  if (!stage || !layer) return;

  const gridInfo = layer.gridInfo as GridInfo;
  if (!gridInfo) return;

  // 清除现有的像素块（保留网格）
  const pixelBlocks = layer.children.filter(
    (child) =>
      child.attrs.pixelX !== undefined && child.attrs.pixelY !== undefined
  );
  pixelBlocks.forEach((block) => block.destroy());

  // 从ImageData重新创建像素块
  restorePixelBlocksFromImageData(imageData, gridInfo);

  layer.batchDraw();
};

const restorePixelBlocksFromImageData = (
  imageData: ImageData,
  gridInfo: GridInfo
) => {
  if (!layer) return;

  const { startX, startY, pixelSize } = gridInfo;
  const data = imageData.data;

  for (let y = 0; y < imageData.height; y++) {
    for (let x = 0; x < imageData.width; x++) {
      const index = (y * imageData.width + x) * 4;
      const r = data[index];
      const g = data[index + 1];
      const b = data[index + 2];
      const a = data[index + 3];

      // 只重建非透明像素
      if (a > 0) {
        const pixel = new Konva.Rect({
          x: startX + x * pixelSize,
          y: startY + y * pixelSize,
          width: pixelSize,
          height: pixelSize,
          fill: `rgba(${r}, ${g}, ${b}, ${a / 255})`,
        });
        pixel.setAttrs({ pixelX: x, pixelY: y });
        layer.add(pixel);
      }
    }
  }
};

const handleResize = () => {
  if (stage && stageContainer.value) {
    const containerRect = stageContainer.value.getBoundingClientRect();
    stage.width(containerRect.width);
    stage.height(containerRect.height);

    // 当stage尺寸变化时，重新创建网格以确保居中
    if (layer) {
      // 保存当前像素数据
      const currentPixels = layer.children.filter(
        (child) =>
          child.attrs.pixelX !== undefined && child.attrs.pixelY !== undefined
      );

      // 清除所有子元素
      layer.destroyChildren();

      // 重新创建网格
      createPixelGrid();

      // 重新创建像素块
      if (props.currentFrame && layer) {
        const gridInfo = layer.gridInfo as GridInfo;
        if (gridInfo) {
          currentPixels.forEach((pixel) => {
            const pixelX = pixel.attrs.pixelX;
            const pixelY = pixel.attrs.pixelY;
            const fill = (pixel as Konva.Rect).fill();

            const newPixel = new Konva.Rect({
              x: gridInfo.startX + pixelX * gridInfo.pixelSize,
              y: gridInfo.startY + pixelY * gridInfo.pixelSize,
              width: gridInfo.pixelSize,
              height: gridInfo.pixelSize,
              fill: fill,
            });
            newPixel.setAttrs({ pixelX, pixelY });
            layer!.add(newPixel);
          });
        }
      }
    }

    stage.batchDraw();
  }
};

const resetView = () => {
  if (stage) {
    viewport.resetView(stage);
  }
};

// 监听当前帧变化
watch(
  () => props.currentFrame,
  () => {
    loadCurrentFrame();
  }
);

// 监听模式变化，重置状态
watch(
  () => props.mode,
  (newMode, oldMode) => {
    // 模式切换时重置相关状态
    isDrawing = false;
    lastPointerPosition = null;

    if (oldMode === EditorMode.PAINT && newMode === EditorMode.ZOOM) {
      // 从编辑模式切换到缩放模式时，重置多点触控状态
      resetMultiTouchState();
      // 注意：不要重置touchInfo，因为在多点触控检测时可能已经初始化了
    } else {
      // 其他模式切换时才重置触摸信息
      touchInfo = { initialDistance: 0, initialScale: 1 };
    }
  }
);

// 暴露方法给父组件
defineExpose({
  restoreFromHistory,
  resetView,
  getCurrentSpriteImageData,
  handleResize,
});
</script>

<style scoped>
.pixelated {
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.pixelated canvas {
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}
</style>
